import { useCallback, useEffect, useState } from 'react';
import {
  ROTULO_DA_EXCECAO,
  hojeBrasilia,
  lerExcecoes,
  lerMovimentacao,
  lerVisao,
  menosDias,
} from '../../central';
import type {
  Excecoes,
  Fontes,
  Movimentacao,
  PontoDaEvolucao,
  TipoDeExcecao,
  VisaoGeral as Dados,
} from '../../central';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { CartaoDeGrafico } from '../../design/graficos/CartaoDeGrafico';
import { agrupar } from '../../design/graficos/agrupar';
import {
  MODELOS_QUANDO,
  MODELOS_TEMPO,
  fmt,
  quandoVende,
  serieNoTempo,
} from '../../design/graficos/modelos';
import type { Formato } from '../../design/graficos/modelos';
import { lerGaleria } from '../../paineis';
import type { GaleriaDeGraficos } from '../../paineis';
import { MaisVendidos } from '../paineis/Home';
import {
  BarraDeFiltros,
  Kpi,
  diaCurto,
  lerFiltros,
  pctVar,
  textoDoPeriodo,
} from '../paineis/pecasDaVisao';
import { SubnavDeVendas } from './SubnavDeVendas';
import { TabelaDeMovimentacao } from './Movimentacao';
import './central.css';

/**
 * TELA 13 — VENDAS, VISÃO GERAL. Os números do período (filtro no endereço: hoje, ontem, semana,
 * mês, trimestre, ano ou livre), cada um com a fonte e contra uma base dita: hoje, a média dos 4
 * mesmos dias da semana ATÉ A MESMA HORA; um período, o anterior de mesmo tamanho. A margem vem do
 * custo que a API do HOS manda por item (desde 12/09) e diz quanto do faturamento tem custo.
 * Um gráfico só para faturamento, cupons, ticket, itens e margem — troca a métrica e o modelo.
 */

type Metrica = 'faturamento' | 'cupons' | 'ticket' | 'itens' | 'margem';
const METRICAS: readonly { m: Metrica; rotulo: string; formato: Formato }[] = [
  { m: 'faturamento', rotulo: 'Faturamento', formato: 'reais' },
  { m: 'cupons', rotulo: 'Cupons', formato: 'inteiro' },
  { m: 'ticket', rotulo: 'Ticket', formato: 'reais' },
  { m: 'itens', rotulo: 'Itens', formato: 'inteiro' },
  { m: 'margem', rotulo: 'Margem', formato: 'pct' },
];

const lembrada = (): Metrica => {
  try {
    const v = window.localStorage.getItem('inovapay:vendas:metrica');
    return METRICAS.some((x) => x.m === v) ? (v as Metrica) : 'faturamento';
  } catch {
    return 'faturamento';
  }
};

export function VisaoGeralDeVendas({ parametros }: { parametros: URLSearchParams }) {
  const hoje = hojeBrasilia();
  const f = lerFiltros(parametros, hoje);
  const { de, ate } = f.periodo;
  const [d, setD] = useState<Dados | null>(null);
  const [mov, setMov] = useState<Movimentacao | null>(null);
  const [exc, setExc] = useState<Excecoes | null>(null);
  const [g, setG] = useState<GaleriaDeGraficos | null>(null);
  const [erro, setErro] = useState(false);
  // Cada bloco tem o SEU erro: falha na movimentação não pode virar "carregando…" para sempre.
  const [erroMov, setErroMov] = useState(false);
  const [erroG, setErroG] = useState(false);
  const [erroExc, setErroExc] = useState(false);

  const carregarMov = useCallback(() => {
    setErroMov(false);
    setMov(null);
    void lerMovimentacao(de, ate, { tipo: 'venda' }).then((m) =>
      m.ok ? setMov(m.dados) : setErroMov(true),
    );
  }, [de, ate]);
  const carregarG = useCallback(() => {
    setErroG(false);
    setG(null);
    void lerGaleria({ de, ate }).then((r) => (r.ok ? setG(r.dados) : setErroG(true)));
  }, [de, ate]);
  const carregarExc = useCallback(() => {
    setErroExc(false);
    void lerExcecoes(menosDias(hoje, 6), hoje).then((e) =>
      e.ok ? setExc(e.dados) : setErroExc(true),
    );
  }, [hoje]);
  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerVisao(de, ate).then((v) => (v.ok ? setD(v.dados) : setErro(true)));
    carregarMov();
    carregarG();
    carregarExc();
  }, [de, ate, carregarMov, carregarG, carregarExc]);
  useEffect(carregar, [carregar]);

  const abertas = exc ? exc.excecoes.filter((x) => x.status !== 'justificada').length : undefined;
  const quando = textoDoPeriodo(f.filtro, f.periodo, hoje);
  const doPeriodo = d ? d.evolucao.filter((p) => p.dia >= de && p.dia <= ate) : [];
  const base = d?.comparacao?.rotulo ?? '';
  const var_ = (atual: number, antes: number | null | undefined) =>
    antes ? pctVar(Math.round(((atual - antes) / antes) * 1000) / 10, base) : null;
  const semHos = d !== null && d.fonte !== 'relatorio_hos';

  return (
    <section className="cv">
      <SubnavDeVendas
        atual="vendas"
        excecoes={abertas}
        direita={
          d?.fontes.apiHos.ultimaVenda
            ? `última venda lida ${hora(d.fontes.apiHos.ultimaVenda)}`
            : undefined
        }
      />
      <div className="cv-corpo vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <h1>Vendas</h1>
            <div className="vg-contexto">
              {d?.comparacao ? `Comparado com ${d.comparacao.rotulo}` : 'Rede Inova · Muriaé'}
            </div>
          </div>
          <div className="vg-acoes">
            <a className="vg-botao" href="#/movimentacao">
              <Icone nome="receipt" tamanho={15} traco={1.6} />
              Movimentação
            </a>
            <a className="vg-botao" href="#/excecoes">
              <Icone nome="warn" tamanho={15} traco={1.6} />
              Exceções
            </a>
          </div>
        </header>

        <BarraDeFiltros f={f} hoje={hoje} rota="vendas" />
        {d && <FontesDeVenda f={d.fontes} />}

        {erro ? (
          <ErroDeArea oQue="as vendas" aoTentar={carregar} />
        ) : (
          <>
            <div className="vg-kpis">
              <Kpi
                rotulo={`Faturamento · ${quando}`}
                icone="money"
                valor={
                  d
                    ? d.faturamento.disponivel
                      ? fmt(Number(d.faturamento.valor), 'reais')
                      : '—'
                    : null
                }
                variacao={
                  d && d.faturamento.disponivel
                    ? var_(Number(d.faturamento.valor), d.comparacao?.faturamento)
                    : null
                }
                sub={d && !d.faturamento.disponivel ? d.faturamento.porque : null}
                trilha={doPeriodo.map((p) => p.faturamento)}
                link="#/movimentacao"
                destaque
              />
              <Kpi
                rotulo="Cupons"
                icone="users"
                valor={d ? (d.cupons.disponivel ? fmt(d.cupons.valor, 'inteiro') : '—') : null}
                variacao={null}
                sub={d ? (d.cupons.disponivel ? 'clientes atendidos' : d.cupons.porque) : null}
                trilha={doPeriodo.map((p) => p.cupons)}
                link="#/movimentacao"
              />
              <Kpi
                rotulo="Ticket médio"
                icone="receipt"
                valor={
                  d
                    ? d.ticketMedio.disponivel
                      ? fmt(Number(d.ticketMedio.valor), 'reais')
                      : '—'
                    : null
                }
                variacao={
                  d && d.ticketMedio.disponivel
                    ? var_(Number(d.ticketMedio.valor), d.comparacao?.ticket)
                    : null
                }
                sub={d && !d.ticketMedio.disponivel ? d.ticketMedio.porque : null}
                trilha={doPeriodo.map((p) => p.ticket ?? 0)}
                link="#/movimentacao"
              />
              <Kpi
                rotulo="Itens"
                icone="box"
                valor={d ? (d.itens.disponivel ? fmt(d.itens.valor, 'inteiro') : '—') : null}
                variacao={null}
                sub={d ? (d.itens.disponivel ? 'unidades vendidas' : d.itens.porque) : null}
                trilha={doPeriodo.map((p) => p.itens)}
                link="#/movimentacao"
              />
              <Kpi
                rotulo="Itens por cupom"
                icone="pill"
                valor={
                  d
                    ? d.itensPorCupom.disponivel
                      ? fmt(Number(d.itensPorCupom.valor), 'decimal')
                      : '—'
                    : null
                }
                variacao={null}
                sub={
                  d
                    ? d.itensPorCupom.disponivel
                      ? 'quantos itens cada cliente leva'
                      : d.itensPorCupom.porque
                    : null
                }
                trilha={doPeriodo.map((p) => (p.cupons ? p.itens / p.cupons : 0))}
                link="#/movimentacao"
              />
              <Kpi
                rotulo="Margem"
                icone="gauge"
                valor={d ? (d.margem.disponivel ? fmt(Number(d.margem.valor), 'pct') : '—') : null}
                variacao={null}
                sub={
                  d
                    ? d.margem.disponivel
                      ? `custo em ${fmt(d.margem.cobertura ?? 0, 'pct')} do faturamento`
                      : d.margem.porque
                    : null
                }
                trilha={doPeriodo.map((p) => p.margem ?? 0)}
                link="#/precos"
              />
            </div>

            <div className="vg-linha vg-linha-principal">
              <div className="vg-grafico">
                {d ? (
                  <Evolucao d={d} semHos={semHos} />
                ) : (
                  <div className="gx-cartao vg-osso" style={{ height: 400 }} aria-busy="true" />
                )}
              </div>
              {erroExc && !exc ? (
                <div className="hm-cartao vg-atencao cv-fora-novo">
                  <ErroDeArea oQue="o que está fora do padrão" aoTentar={carregarExc} />
                </div>
              ) : (
                <ForaDoPadrao exc={exc} abertas={abertas} />
              )}
            </div>

            {erroG && !g ? (
              <ErroDeArea oQue="os gráficos do período" aoTentar={carregarG} />
            ) : (
              <div className="vg-linha cv-linha-2">
                {g ? (
                  <QuandoVende g={g} quando={quando} />
                ) : (
                  <div className="gx-cartao vg-osso" aria-busy="true" />
                )}
                <MaisVendidos g={g} quando={quando} />
              </div>
            )}

            <div className="vg-bloco cv-mov-previa">
              <div className="vg-bloco-topo">
                <div>
                  <h2>Movimentação</h2>
                  <p>
                    {mov
                      ? `${fmt(mov.cupons, 'inteiro')} cupons · ${fmt(mov.itens, 'inteiro')} itens · ${quando}`
                      : erroMov
                        ? 'não carregou'
                        : 'carregando…'}
                  </p>
                </div>
                <a href="#/movimentacao" className="vg-bloco-link">
                  Ver completa
                  <Icone nome="chev-r" tamanho={13} traco={1.8} />
                </a>
              </div>
              {erroMov && !mov && <ErroDeArea oQue="a movimentação" aoTentar={carregarMov} />}
              {mov && (
                <TabelaDeMovimentacao
                  linhas={mov.linhas.slice(0, 8)}
                  vazio={
                    mov.fonteDisponivel
                      ? 'Nenhum cupom no período.'
                      : 'Nenhuma venda do HOS no período.'
                  }
                />
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

const hora = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });

/** De onde vem cada número e de quando — a API do HOS, as devoluções e os lançamentos. */
function FontesDeVenda({ f }: { f: Fontes }) {
  const dia = (s: string | null) => (s ? diaCurto(s) : '—');
  return (
    <div className="vg-fontes" aria-label="Fontes das vendas">
      <span className={`vg-fonte vg-fonte-${f.apiHos.integrada ? 'ok' : 'fora'}`}>
        <i />
        API do HOS
        <b>
          {f.apiHos.integrada && f.apiHos.ultimaVenda
            ? `desde ${dia(f.apiHos.desde)} · última venda ${hora(f.apiHos.ultimaVenda)}`
            : 'sem venda lida'}
        </b>
      </span>
      <span className={`vg-fonte vg-fonte-${f.relatorioHos.disponivel ? 'ok' : 'fora'}`}>
        <i />
        Vendas gravadas
        <b>
          {f.relatorioHos.disponivel
            ? `${dia(f.relatorioHos.desde)}/${(f.relatorioHos.desde ?? '').slice(0, 4)} a ${dia(f.relatorioHos.ate)}`
            : 'nenhuma'}
        </b>
      </span>
      <span className={`vg-fonte vg-fonte-${f.devolucoes.disponivel ? 'ok' : 'fora'}`}>
        <i />
        Devoluções
        <b>
          {f.devolucoes.disponivel && f.devolucoes.importadoEm
            ? `${f.devolucoes.fonte === 'hos_api' ? 'API do HOS' : 'PDF'} · ${hora(f.devolucoes.importadoEm)}`
            : 'nenhuma lida'}
        </b>
      </span>
      <span className={`vg-fonte vg-fonte-${f.inovapay.disponivel ? 'ok' : 'fora'}`}>
        <i />
        Lançamentos no InovaPay
        <b>{f.inovapay.ultima ? hora(f.inovapay.ultima) : 'nenhum'}</b>
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * O gráfico único: troca a MÉTRICA e o MODELO
 * ------------------------------------------------------------------ */

function Evolucao({ d, semHos }: { d: Dados; semHos: boolean }) {
  const [metrica, setMetrica] = useState<Metrica>(lembrada);
  const escolher = (m: Metrica) => {
    setMetrica(m);
    try {
      window.localStorage.setItem('inovapay:vendas:metrica', m);
    } catch {
      // vale só nesta visita
    }
  };
  const info = METRICAS.find((x) => x.m === metrica) ?? METRICAS[0];
  const serie = serieDaMetrica(d.evolucao, metrica);
  const valores = serie.atual.filter((v): v is number => v !== null);
  const total =
    metrica === 'faturamento' || metrica === 'cupons' || metrica === 'itens'
      ? valores.reduce((a, b) => a + b, 0)
      : valores.length
        ? valores.reduce((a, b) => a + b, 0) / valores.length
        : null;
  const melhor = serie.cats[serie.atual.indexOf(Math.max(...valores))];
  const bloqueada = (m: Metrica) =>
    (semHos && (m === 'cupons' || m === 'ticket')) ||
    (m === 'margem' && !d.evolucao.some((p) => p.margem !== null));
  const unidade = serie.agrupado === 'dia' ? 'dia' : serie.agrupado === 'semana' ? 'semana' : 'mês';
  return (
    <CartaoDeGrafico
      id={`cv-evolucao-${metrica}`}
      titulo={`Evolução · ${info?.rotulo.toLowerCase() ?? ''}`}
      numero={
        total === null
          ? '—'
          : `${fmt(total, info?.formato ?? 'reais')}${metrica === 'ticket' || metrica === 'margem' ? ' em média' : ''}`
      }
      conclusao={
        valores.length ? (
          <>
            Melhor {unidade}: <strong>{melhor}</strong> (
            {fmt(Math.max(...valores), info?.formato ?? 'reais')}).{' '}
            {metrica === 'margem'
              ? 'Margem dos itens com custo da API do HOS; dia sem custo fica sem ponto.'
              : 'Tracejado: a média do mesmo dia da semana nas 4 semanas anteriores.'}
          </>
        ) : (
          'Nenhum dado desta métrica no período.'
        )
      }
      modelos={MODELOS_TEMPO}
      padrao={metrica === 'margem' ? 'linha' : 'area'}
      desenhar={(m) =>
        serieNoTempo(m, {
          cats: serie.cats,
          atual: serie.atual,
          anterior: serie.ref,
          nome: info?.rotulo ?? '',
          nomeAnterior: 'Média do mesmo dia (4 semanas)',
          formato: info?.formato ?? 'reais',
        })
      }
      fonte="Fonte: vendas do HOS (API, automática)"
      vazio={valores.length ? null : 'Nenhum dado desta métrica no período.'}
      altura={300}
      topo={
        <div className="cv-metricas" role="group" aria-label="Métrica do gráfico">
          {METRICAS.map((x) => (
            <button
              key={x.m}
              type="button"
              className={`vg-seg${metrica === x.m ? ' vg-seg-ativo' : ''}`}
              aria-pressed={metrica === x.m}
              disabled={bloqueada(x.m)}
              title={bloqueada(x.m) ? 'sem dado desta métrica no período' : undefined}
              onClick={() => escolher(x.m)}
            >
              {x.rotulo}
            </button>
          ))}
        </div>
      }
    />
  );
}

/** A série da métrica: dia a dia até 62 dias; depois por semana e por mês (sem perder centavo). */
function serieDaMetrica(pontos: readonly PontoDaEvolucao[], m: Metrica) {
  const blocos = agrupar(pontos);
  const agrupado = pontos.length <= 62 ? 'dia' : pontos.length <= 184 ? 'semana' : 'mes';
  const refs = agrupar(
    pontos.map((p) => ({
      dia: p.dia,
      faturamento: p.referencia.faturamento ?? 0,
      cupons: p.referencia.cupons ?? 0,
      itens: p.referencia.itens ?? 0,
    })),
  );
  const porDia = new Map(pontos.map((p) => [p.dia, p]));
  const temRef = pontos.some((p) => p.referencia.faturamento !== null);
  const atual = blocos.map((b) => {
    if (m === 'margem') {
      const xs = b.dias.map((x) => porDia.get(x)?.margem).filter((v): v is number => v != null);
      return xs.length ? Math.round((xs.reduce((a, c) => a + c, 0) / xs.length) * 10) / 10 : null;
    }
    if (m === 'ticket') return b.ticket;
    return b[m];
  });
  const ref =
    m === 'margem' || !temRef
      ? blocos.map(() => null)
      : refs.map((b) => (m === 'ticket' ? b.ticket : b[m]));
  return { cats: blocos.map((b) => b.rotulo), atual, ref, agrupado };
}

/* ------------------------------------------------------------------ *
 * Fora do padrão (exceções dos últimos 7 dias)
 * ------------------------------------------------------------------ */

function ForaDoPadrao({ exc, abertas }: { exc: Excecoes | null; abertas: number | undefined }) {
  const tipos = exc ? (Object.keys(exc.contagem) as TipoDeExcecao[]) : [];
  return (
    <div className="hm-cartao vg-atencao cv-fora-novo">
      <div className="hm-cartao-topo">
        <h2>Fora do padrão · 7 dias</h2>
        {abertas !== undefined && abertas > 0 && <span className="hm-bola">{abertas}</span>}
      </div>
      {!exc ? (
        <div className="vg-lista-osso" style={{ padding: 16 }} aria-busy="true">
          <i />
          <i />
          <i />
        </div>
      ) : (
        <ul className="cv-fora-lista">
          {tipos.map((t) => (
            <li key={t}>
              <a href={`#/excecoes?tipo=${t}`}>
                <span className={`cv-fora-num${exc.contagem[t] ? ' cv-fora-tem' : ''}`}>
                  {exc.contagem[t]}
                </span>
                <span className="cv-fora-texto">
                  <strong>{ROTULO_DA_EXCECAO[t]}</strong>
                </span>
                <Icone nome="chev-r" tamanho={14} traco={1.6} />
              </a>
            </li>
          ))}
          {exc.desligadas.length > 0 && (
            <li className="cv-fora-desligada" title={exc.desligadasPorque}>
              <span className="cv-fora-num">
                <Icone nome="lock" tamanho={13} traco={1.6} />
              </span>
              <span className="cv-fora-texto">
                <strong>{exc.desligadas.join(' · ')}</strong>
                <span>{exc.desligadasPorque}</span>
              </span>
            </li>
          )}
        </ul>
      )}
      <a className="hm-cheque" href="#/excecoes">
        <Icone nome="search" tamanho={16} traco={1.6} />
        <span>
          <b>Analisar as exceções</b> · em análise, justificar, histórico
        </span>
        <strong>Abrir</strong>
      </a>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Quando a loja vende (hora × dia da semana)
 * ------------------------------------------------------------------ */

function QuandoVende({ g, quando }: { g: GaleriaDeGraficos; quando: string }) {
  const pico = [...g.porHora].sort((a, b) => b.cupons - a.cupons)[0];
  const umDia = g.periodo.de === g.periodo.ate;
  return (
    <CartaoDeGrafico
      id="cv-quando"
      titulo={`Quando a loja vende · ${quando}`}
      numero={pico ? `pico às ${String(pico.hora)}h` : '—'}
      conclusao={
        pico ? (
          <>
            O horário mais forte teve <strong>{fmt(pico.cupons, 'inteiro')} cupons</strong> (
            {fmt(pico.faturamento, 'reais')}). Use para escalar a equipe no balcão.
          </>
        ) : (
          'Nenhuma venda do HOS no período.'
        )
      }
      modelos={MODELOS_QUANDO}
      padrao={umDia ? 'colunas_hora' : 'calor'}
      desenhar={(m) => quandoVende(m, { mapa: g.mapaDeCalor, porHora: g.porHora })}
      fonte="Fonte: vendas do HOS (API, automática)"
      vazio={pico ? null : 'Nenhuma venda do HOS no período.'}
      altura={270}
    />
  );
}
